import type { ProductStatus, StockState, UnitRefDTO } from '@lightbm/shared';
import { useTranslation } from 'react-i18next';
import { useFormatters } from '@/app/app-context';
import { Badge, type BadgeTone } from '@/components/ui/Badge';
import { cn } from '@/lib/cn';
import { initialsOf } from '@/lib/person';

const STATE_TONES: Record<StockState, BadgeTone> = {
  in_stock: 'success',
  low: 'warning',
  out: 'neutral',
  negative: 'danger',
  overstock: 'info',
};

/** Stock state: always a colour and a word (UI kit). */
export function StockStateBadge({ state, status }: { state: StockState; status?: ProductStatus }) {
  const { t } = useTranslation('products');
  if (status === 'archived') return <Badge tone="neutral">{t('status.archived')}</Badge>;
  return <Badge tone={STATE_TONES[state]}>{t(`states.${state}`)}</Badge>;
}

const THUMB_TONES = [
  'bg-primary-soft text-primary',
  'bg-info-soft text-info',
  'bg-warning-soft text-warning',
  'bg-reversed-soft text-reversed',
  'bg-neutral-soft text-ink-2',
];

const SIZES = { sm: 'size-11 text-sm', md: 'size-14 text-base', lg: 'size-24 text-2xl sm:size-28' };

/** Product photo, or its initials on a stable colour (designs: "LB", "EC"...). */
export function ProductThumb({
  name,
  photoUrl,
  size = 'sm',
  className,
}: {
  name: string;
  photoUrl: string | null;
  size?: keyof typeof SIZES;
  className?: string;
}) {
  if (photoUrl) {
    return (
      <img
        src={photoUrl}
        alt=""
        className={cn(
          'shrink-0 rounded-control border border-line object-cover',
          SIZES[size],
          className,
        )}
      />
    );
  }
  let hash = 0;
  for (const char of name) hash = (hash * 31 + char.charCodeAt(0)) >>> 0;
  return (
    <span
      aria-hidden="true"
      className={cn(
        'flex shrink-0 items-center justify-center rounded-control font-semibold',
        THUMB_TONES[hash % THUMB_TONES.length],
        SIZES[size],
        className,
      )}
    >
      {initialsOf(name)}
    </span>
  );
}

/** Quantities with the decimals of their unit: "534", "1 245,500". */
export function useQuantity() {
  const format = useFormatters();
  return (value: number, unit: Pick<UnitRefDTO, 'decimals'>) => format.number(value, unit.decimals);
}

/** "Electrical › Lighting" */
export const categoryLabel = (category: { path: string[] } | null) =>
  category ? category.path.join(' › ') : '—';

/** "Min / Max" cell: "100 / 600", "10 / —", "— / —" */
export function useLimits() {
  const quantity = useQuantity();
  return (min: number | null, max: number | null, unit: Pick<UnitRefDTO, 'decimals'>) =>
    `${min === null || min === 0 ? '—' : quantity(min, unit)} / ${max === null ? '—' : quantity(max, unit)}`;
}
